import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	effect,
	inject,
	input,
	output,
	signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize, forkJoin, of } from 'rxjs';

import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule, MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';

import { VindicationApiService } from '@/vindication/vindication.api.service';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { MessageTemplate } from '@/common/message-generate/message-generate.model';
import { ContractorMailTemplatePayload, ContractorMailTemplateResponse } from '@/vindication/vindication.model';

type TemplateRowValue = {
	id: number | null;
	rowId: string;
	templateId: number | null;
	days: number | null;
};

type TemplateRowForm = FormGroup<{
	id: FormControl<number | null>;
	rowId: FormControl<string>;
	templateId: FormControl<number | null>;
	days: FormControl<number | null>;
}>;

@Component({
	selector: 'app-contractor-templates',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatCardModule,
		MatFormFieldModule,
		MatSelectModule,
		MatInputModule,
		MatButtonModule,
		MatDividerModule,
		MatTooltipModule,
	],
	templateUrl: './contractor-templates.component.html',
	styleUrls: ['./contractor-templates.component.scss'],
	providers: [
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: { subscriptSizing: 'dynamic' },
		},
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContractorTemplatesComponent {
	readonly contractorId = input.required<number>();
	readonly templates = input.required<MessageTemplate[]>();
	readonly contractorTemplates = input.required<ContractorMailTemplateResponse[]>();

	readonly saved = output<void>();

	readonly isSaving = signal(false);
	readonly rowCounter = signal(0);

	readonly templateRows: FormArray<TemplateRowForm>;
	readonly templatesForm: FormGroup<{
		rows: FormArray<TemplateRowForm>;
	}>;

	private readonly vindicationApiService = inject(VindicationApiService);
	private readonly fb = inject(FormBuilder);
	private readonly infobox = inject(InfoboxService);
	private readonly cdr = inject(ChangeDetectorRef);

	constructor() {
		this.templateRows = this.fb.array<TemplateRowForm>([]);
		this.templatesForm = this.fb.group({
			rows: this.templateRows,
		});

		effect(
			() => {
				this.applyContractorTemplates(this.contractorTemplates());
			},
			{ allowSignalWrites: true },
		);
	}

	addTemplateRow(): void {
		const next = this.rowCounter() + 1;
		this.rowCounter.set(next);

		this.templateRows.push(
			this.createRow({
				id: null,
				rowId: `R${next}`,
				templateId: null,
				days: null,
			}),
		);
	}

	removeTemplateRow(index: number): void {
		this.templateRows.removeAt(index);
		this.templatesForm.markAllAsTouched();
	}

	saveTemplates(): void {
		this.templatesForm.markAllAsTouched();

		if (this.templateRows.length === 0 || this.templatesForm.invalid) {
			this.infobox.warning('Uzupełnij wszystkie pola zanim zapiszesz szablony');
			return;
		}

		const contractorId = this.contractorId();
		if (!contractorId) {
			return;
		}

		const rows = this.templateRows.getRawValue();

		if (this.hasDuplicateTemplateId(rows)) {
			this.infobox.warning('Nie możesz wybrać tego samego szablonu kilka razy');
			return;
		}

		const existingIds = new Set(this.contractorTemplates().map((t) => t.id));

		const { toCreate, toUpdate, keptIds } = rows.reduce(
			(acc, row) => {
				if (row.id == null) {
					acc.toCreate.push(this.buildPayload(row, contractorId));
					return acc;
				}

				acc.toUpdate.push({ id: row.id, payload: this.buildPayload(row, contractorId) });
				acc.keptIds.add(row.id);
				return acc;
			},
			{
				toCreate: [],
				toUpdate: [],
				keptIds: new Set<number>(),
			},
		);

		const idsToDelete = [...existingIds].filter((id) => !keptIds.has(id));

		const createRequests = toCreate.map((payload) =>
			this.vindicationApiService.createContractorMailTemplate(payload),
		);

		const updateRequests = toUpdate.map(({ id, payload }) =>
			this.vindicationApiService.updateContractorMailTemplate(payload, id),
		);

		const deleteRequests = idsToDelete.map((id) => this.vindicationApiService.removeContractorMailTemplate(id));

		const requests = [...createRequests, ...updateRequests, ...deleteRequests];

		this.isSaving.set(true);

		const save$ = requests.length ? forkJoin(requests) : of([]);

		save$.pipe(finalize(() => this.isSaving.set(false))).subscribe(() => {
			this.infobox.success('Szablony zapisane pomyślnie');
			this.saved.emit();
		});
	}

	private hasDuplicateTemplateId(rows: TemplateRowValue[]): boolean {
		const seen = new Set<number | string>();

		for (const row of rows) {
			const id = row?.templateId;
			if (id == null) {
				continue;
			}

			if (seen.has(id)) {
				return true;
			}

			seen.add(id);
		}

		return false;
	}

	private buildPayload(row: TemplateRowValue, contractorId: number): ContractorMailTemplatePayload {
		return {
			contractor: contractorId,
			mailTemplate: row.templateId,
			daysToSend: row.days,
		};
	}

	private applyContractorTemplates(contractorTemplates: ContractorMailTemplateResponse[]): void {
		this.templateRows.clear();

		let counter = 0;

		for (const ct of contractorTemplates) {
			counter += 1;

			this.templateRows.push(
				this.createRow({
					rowId: `R${counter}`,
					id: ct.id ?? null,
					templateId: ct.mailTemplate?.id ?? null,
					days: ct.daysToSend ?? null,
				}),
			);
		}

		this.rowCounter.set(counter);

		this.templatesForm.markAsPristine();
		this.templatesForm.markAsUntouched();
		this.cdr.detectChanges();
	}

	private createRow(value: TemplateRowValue): TemplateRowForm {
		return this.fb.group({
			id: this.fb.control<number | null>(value.id),
			rowId: this.fb.nonNullable.control(value.rowId),
			templateId: this.fb.control<number | null>(value.templateId, { validators: [Validators.required] }),
			days: this.fb.control<number | null>(value.days, { validators: [Validators.required] }),
		});
	}
}
